/**
 * Caméra de gestion : orbite autour d'un point du terrain (déplacement au
 * clavier ZQSD/WASD ou flèches, glisser-déposer, molette pour zoomer, clic
 * droit pour tourner), plus une vue libre (survol) à la demande.
 * Mouvements amortis ; la caméra ne passe jamais sous le relief.
 */
import * as THREE from 'three/webgpu';
import type { Heightfield } from '@snowchain/shared';
import { mapViewScale } from './lod';

/** Cadrage de départ : point visé au sol et cap de la caméra. */
export interface CameraHome {
  x: number;
  z: number;
  yaw: number;
}

export class RtsCamera {
  readonly camera: THREE.PerspectiveCamera;
  /** Point visé au sol. */
  readonly target = new THREE.Vector3(110, 0, 600);
  private goal = { x: 120, z: 330, dist: 760, yaw: 0.42, pitch: 0.5 };
  private cur = { x: 120, z: 330, dist: 760, yaw: 0.42, pitch: 0.5 };
  private keys = new Set<string>();
  private drag: { mode: 'pan' | 'rotate'; x: number; y: number } | null = null;
  free = false;
  private freePos = new THREE.Vector3();
  private freeYaw = 0;
  private freePitch = -0.2;
  /** Vrai pendant un glisser (le clic n'est alors pas une sélection). */
  dragged = false;
  enabled = true;
  /** Recul maximal à la molette (m) : un peu plus grand sur les grandes cartes. */
  readonly maxDist: number;
  /** Cadrage de la vue d'ensemble [x, z, recul], fourni par le moteur (emprise du domaine). */
  overviewFrame: (() => [number, number, number]) | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly hf: Heightfield,
    home?: CameraHome,
  ) {
    this.camera = new THREE.PerspectiveCamera(50, canvas.clientWidth / Math.max(1, canvas.clientHeight), 2, 16_000);
    this.maxDist = Math.round(2600 * mapViewScale(hf.half));
    if (home) {
      this.goal = { ...this.goal, x: home.x, z: home.z, yaw: home.yaw };
      this.cur = { ...this.goal };
      this.target.set(home.x, 0, home.z);
    }
    this.bind();
  }

  get distance(): number {
    return this.cur.dist;
  }

  setFree(on: boolean): void {
    if (on === this.free) return;
    this.free = on;
    if (on) {
      this.freePos.copy(this.camera.position);
      this.freeYaw = Math.atan2(this.target.x - this.camera.position.x, this.target.z - this.camera.position.z);
      this.freePitch = -0.25;
    }
  }

  focus(x: number, z: number, dist?: number): void {
    this.goal.x = x;
    this.goal.z = z;
    if (dist !== undefined) this.goal.dist = dist;
    if (this.free) this.setFree(false);
  }

  /** Vue de dessus (plan du domaine). */
  overview(): void {
    const [x, z, dist] = this.overviewFrame?.() ?? [40, -60, 2300];
    this.view(x, z, dist, 1.32, 0);
  }

  /** Place la caméra (mesures reproductibles) : cible, distance, inclinaison, cap. */
  view(x: number, z: number, dist: number, pitch: number, yaw: number, instant = false): void {
    this.goal = { x, z, dist, pitch, yaw };
    if (instant) this.cur = { ...this.goal };
    if (this.free) this.setFree(false);
  }

  /** Vue libre posée en (x, z), à `height` m du sol, regard selon `yaw` et `pitch`. */
  placeFree(x: number, z: number, height: number, yaw: number, pitch: number): void {
    this.free = true;
    this.freePos.set(x, this.hf.height(x, z) + height, z);
    this.freeYaw = yaw;
    this.freePitch = pitch;
  }

  private bind(): void {
    const c = this.canvas;
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    c.addEventListener('pointerdown', (e) => {
      if (!this.enabled) return;
      this.dragged = false;
      if (e.button === 2 || e.button === 1) this.drag = { mode: 'rotate', x: e.clientX, y: e.clientY };
      else if (e.button === 0) this.drag = { mode: 'pan', x: e.clientX, y: e.clientY };
    });
    window.addEventListener('pointerup', () => {
      this.drag = null;
    });
    window.addEventListener('pointermove', (e) => {
      if (!this.drag) return;
      const dx = e.clientX - this.drag.x;
      const dy = e.clientY - this.drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) this.dragged = true;
      this.drag.x = e.clientX;
      this.drag.y = e.clientY;
      if (this.free) {
        this.freeYaw -= dx * 0.004;
        this.freePitch = Math.max(-1.3, Math.min(1.1, this.freePitch - dy * 0.004));
        return;
      }
      if (this.drag.mode === 'rotate') {
        this.goal.yaw -= dx * 0.005;
        this.goal.pitch = Math.max(0.18, Math.min(1.45, this.goal.pitch + dy * 0.004));
      } else if (this.dragged) {
        const k = this.cur.dist * 0.0016;
        const s = Math.sin(this.cur.yaw);
        const co = Math.cos(this.cur.yaw);
        this.goal.x -= (dx * co + dy * s) * k;
        this.goal.z -= (-dx * s + dy * co) * k;
      }
    });
    c.addEventListener(
      'wheel',
      (e) => {
        if (!this.enabled) return;
        e.preventDefault();
        if (this.free) {
          this.freePos.y += e.deltaY * 0.2;
          return;
        }
        if (e.shiftKey) return;
        this.goal.dist = Math.max(35, Math.min(this.maxDist, this.goal.dist * Math.exp(e.deltaY * 0.0012)));
      },
      { passive: false },
    );
    window.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement | null)?.tagName === 'INPUT') return;
      this.keys.add(e.code);
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
  }

  private key(...codes: string[]): boolean {
    for (const c of codes) if (this.keys.has(c)) return true;
    return false;
  }

  resize(w: number, h: number): void {
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number): void {
    const fast = this.key('ShiftLeft', 'ShiftRight') ? 2.5 : 1;
    // KeyW/KeyA correspondent aux touches physiques : ZQSD en AZERTY, WASD en QWERTY.
    const fwd = (this.key('KeyW', 'ArrowUp') ? 1 : 0) - (this.key('KeyS', 'ArrowDown') ? 1 : 0);
    const side = (this.key('KeyD', 'ArrowRight') ? 1 : 0) - (this.key('KeyA', 'ArrowLeft') ? 1 : 0);
    const rot = (this.key('KeyE') ? 1 : 0) - (this.key('KeyQ') ? 1 : 0);
    if (this.free) {
      const speed = 60 * fast * dt;
      const fx = Math.sin(this.freeYaw);
      const fz = Math.cos(this.freeYaw);
      this.freePos.x += (fx * fwd - fz * side) * speed;
      this.freePos.z += (fz * fwd + fx * side) * speed;
      this.freeYaw -= rot * dt * 1.2;
      const half = this.hf.half;
      this.freePos.x = Math.max(-half * 2, Math.min(half * 2, this.freePos.x));
      this.freePos.z = Math.max(-half * 2, Math.min(half * 2, this.freePos.z));
      const ground = this.hf.height(this.freePos.x, this.freePos.z);
      this.freePos.y = Math.max(ground + 3, Math.min(Math.max(1800, this.hf.maxH + 1000), this.freePos.y));
      this.camera.position.copy(this.freePos);
      const look = new THREE.Vector3(fx * Math.cos(this.freePitch), Math.sin(this.freePitch), fz * Math.cos(this.freePitch));
      this.camera.lookAt(this.freePos.clone().add(look));
      this.target.set(this.freePos.x + fx * 80, ground, this.freePos.z + fz * 80);
      this.camera.updateMatrixWorld();
      return;
    }
    if (fwd || side) {
      const k = this.cur.dist * 0.9 * fast * dt;
      const s = Math.sin(this.cur.yaw);
      const co = Math.cos(this.cur.yaw);
      this.goal.x += (-s * fwd + co * side) * k;
      this.goal.z += (-co * fwd - s * side) * k;
    }
    if (rot) this.goal.yaw -= rot * dt * 1.4;
    const lim = this.hf.half - 40;
    this.goal.x = Math.max(-lim, Math.min(lim, this.goal.x));
    this.goal.z = Math.max(-lim, Math.min(lim, this.goal.z));
    const a = 1 - Math.exp(-dt * 7);
    this.cur.x += (this.goal.x - this.cur.x) * a;
    this.cur.z += (this.goal.z - this.cur.z) * a;
    this.cur.dist += (this.goal.dist - this.cur.dist) * a;
    this.cur.yaw += (this.goal.yaw - this.cur.yaw) * a;
    this.cur.pitch += (this.goal.pitch - this.cur.pitch) * a;
    const ground = this.hf.height(this.cur.x, this.cur.z);
    this.target.set(this.cur.x, ground, this.cur.z);
    const cp = Math.cos(this.cur.pitch);
    const pos = new THREE.Vector3(
      this.cur.x + Math.sin(this.cur.yaw) * cp * this.cur.dist,
      ground + Math.sin(this.cur.pitch) * this.cur.dist,
      this.cur.z + Math.cos(this.cur.yaw) * cp * this.cur.dist,
    );
    const floor = this.hf.height(pos.x, pos.z) + 6;
    if (pos.y < floor) pos.y = floor;
    this.camera.position.copy(pos);
    this.camera.lookAt(this.target);
    this.camera.updateMatrixWorld();
  }

  /** Rayon souris -> point du terrain (marche le long du rayon sur le champ de hauteur). */
  pick(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.camera);
    const o = ray.ray.origin;
    const d = ray.ray.direction;
    let prevT = 0;
    let step = 2;
    for (let t = 2; t < 9000; t += step) {
      const x = o.x + d.x * t;
      const y = o.y + d.y * t;
      const z = o.z + d.z * t;
      if (y < this.hf.height(x, z)) {
        // Affinage par dichotomie.
        let lo = prevT;
        let hi = t;
        for (let i = 0; i < 12; i++) {
          const mid = (lo + hi) / 2;
          const mx = o.x + d.x * mid;
          const mz = o.z + d.z * mid;
          if (o.y + d.y * mid < this.hf.height(mx, mz)) hi = mid;
          else lo = mid;
        }
        const px = o.x + d.x * hi;
        const pz = o.z + d.z * hi;
        if (!this.hf.inBounds(px, pz)) return null;
        return new THREE.Vector3(px, this.hf.height(px, pz), pz);
      }
      prevT = t;
      step = Math.min(24, step * 1.15);
    }
    return null;
  }
}
